import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2, LogOut, User, CreditCard, Award, Wallet, Shirt, FileText, Megaphone, Download, CalendarCheck, ArrowRight, Eye } from "lucide-react";
import SEO from "../../components/SEO";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { fmtDate } from "../../lib/format";

const STATUT_STYLES = {
  "INSCRIPTION EN COURS": "bg-steel/15 text-steel",
  "CATÉGORIE À VALIDER": "bg-amber-100 text-amber-700",
  "ÉCOLE DE VÉLO VALIDÉE": "bg-blue-100 text-blue-700",
  "LICENCIÉ CLUB VALIDÉ": "bg-blue-100 text-blue-700",
  "PAIEMENT EN ATTENTE": "bg-steel/15 text-steel",
  "PAIEMENT REÇU – EN ATTENTE DE VALIDATION": "bg-amber-100 text-amber-700",
  "INSCRIPTION VALIDÉE": "bg-green-100 text-green-700",
  "INSCRIPTION REFUSÉE": "bg-rouge/10 text-rouge",
  "DOSSIER À VÉRIFIER": "bg-amber-100 text-amber-700",
  "ADHÉSION EXPIRÉE": "bg-rouge/10 text-rouge",
};

function Section({ icon: Icon, title, children, testId }) {
  return (
    <section className="bg-white border border-navy/10" data-testid={testId}>
      <div className="flex items-center gap-3 bg-navy text-white px-6 py-4">
        <Icon size={18} className="text-rouge" />
        <h2 className="font-heading uppercase font-semibold tracking-wider text-sm">{title}</h2>
      </div>
      <div className="p-6 sm:p-8">{children}</div>
    </section>
  );
}

function DocGroup({ title, docs, empty, onView, onDownload, groupTestId }) {
  return (
    <div data-testid={groupTestId}>
      <p className="font-heading uppercase tracking-wider text-xs text-rouge mb-3">{title}</p>
      {docs.length === 0 ? (
        <p className="text-steel text-sm" data-testid={`${groupTestId}-empty`}>{empty}</p>
      ) : (
        docs.map((doc) => (
          <div key={doc.id} className="flex flex-wrap items-center justify-between gap-3 py-3 border-b border-navy/10 last:border-0" data-testid={`document-${doc.id.slice(0, 8)}`}>
            <div className="min-w-0 flex-1">
              <p className="text-navy font-medium text-sm">{doc.title}</p>
              <p className="text-xs text-steel mt-0.5">
                {doc.description || "Document officiel du club"}
                {doc.version ? ` · Version ${doc.version}` : ""}
                {doc.updated_at ? ` · mise à jour ${fmtDate(doc.updated_at)}` : ""}
                {doc.saison ? ` · saison ${doc.saison}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onView(doc)}
                data-testid={`document-view-${doc.id.slice(0, 8)}`}
                className="inline-flex items-center gap-2 border border-navy/25 text-navy text-xs font-heading uppercase tracking-wider px-4 py-2.5 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300"
              >
                <Eye size={13} /> Consulter
              </button>
              <button
                onClick={() => onDownload(doc)}
                data-testid={`document-download-${doc.id.slice(0, 8)}`}
                className="inline-flex items-center gap-2 border border-navy/25 text-navy text-xs font-heading uppercase tracking-wider px-4 py-2.5 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300"
              >
                <Download size={13} /> Télécharger
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex flex-wrap justify-between gap-2 py-3 border-b border-navy/10 last:border-0">
      <span className="font-heading uppercase tracking-wider text-[11px] text-steel pt-0.5">{label}</span>
      <span className="text-navy font-medium text-sm text-right">{value}</span>
    </div>
  );
}

export default function EspaceDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [participations, setParticipations] = useState([]);
  const [docs, setDocs] = useState({ club: [], personnel: [] });
  const [selectedMember, setSelectedMember] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ nom: "", prenom: "", date_naissance: "", adresse: "", ville: "", code_postal: "", telephone: "", exactitude: false, conditions: false, reglement: false });
  const [addBusy, setAddBusy] = useState(false);
  const [reglementDoc, setReglementDoc] = useState(null);

  useEffect(() => {
    if (user?.role === "licencie") {
      const params = selectedMember ? { member_id: selectedMember } : {};
      api.get("/espace/tableau-de-bord", { params }).then((r) => setData(r.data)).catch((e) => setError(formatApiError(e)));
      api.get("/espace/participations").then((r) => setParticipations(r.data)).catch(() => {});
      api.get("/espace/documents").then((r) => setDocs(r.data)).catch(() => {});
      api.get("/documents/inscription/obligatoires").then((r) => setReglementDoc((r.data || []).find((d) => d.type === "reglement_interieur") || null)).catch(() => {});
    }
  }, [user, selectedMember]);

  const submitAddLicencie = async (e) => {
    e.preventDefault();
    setAddBusy(true);
    try {
      const { data: res } = await api.post("/espace/licencies", addForm);
      const { data: fresh } = await api.get("/espace/tableau-de-bord", { params: { member_id: res.member.member_id } });
      setData(fresh);
      setSelectedMember(res.member.member_id);
      setShowAdd(false);
      setAddForm({ nom: "", prenom: "", date_naissance: "", adresse: "", ville: "", code_postal: "", telephone: "", exactitude: false, conditions: false, reglement: false });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setAddBusy(false);
    }
  };

  const fetchDocBlob = async (doc, mode) => {
    const r = await api.get(`/espace/documents/${doc.id}/${mode}`, { responseType: "blob" });
    return new Blob([r.data], { type: doc.content_type || "application/pdf" });
  };

  const viewDoc = async (doc) => {
    try {
      const blob = await fetchDocBlob(doc, "consulter");
      window.open(URL.createObjectURL(blob), "_blank", "noopener");
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const blob = await fetchDocBlob(doc, "telecharger");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || doc.title;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-mist flex items-center justify-center" data-testid="dashboard-loading"><Loader2 className="animate-spin text-rouge" size={32} /></div>;
  }
  if (!user) return <Navigate to="/espace/connexion" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;

  const doLogout = async () => {
    await logout();
    navigate("/espace");
  };

  return (
    <div className="min-h-screen bg-mist" data-testid="espace-dashboard">
      <SEO title="Mon espace — Club Cycliste Le Boulou" />
      <header className="bg-navy text-white pt-28 pb-12">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-heading uppercase tracking-[0.3em] text-rouge text-xs mb-2">Espace Licencié</p>
            <h1 className="font-heading uppercase font-semibold tracking-tight text-3xl sm:text-5xl" data-testid="dashboard-welcome">
              Bienvenue {data?.profil?.prenom || user.name?.split(" ")[0] || ""}
            </h1>
          </div>
          <button
            onClick={doLogout}
            data-testid="espace-logout"
            className="inline-flex items-center gap-2 border border-white/25 text-white/80 px-5 py-2.5 text-xs font-heading uppercase tracking-wider hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300"
          >
            <LogOut size={14} /> Se déconnecter
          </button>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-5 sm:px-8 py-12">
        {error && (
          <p data-testid="dashboard-error" className="mb-8 bg-rouge/15 border border-rouge/40 text-rouge text-sm px-4 py-3">{error}</p>
        )}
        {data?.licencies?.length > 0 && (
          <div className="mb-8 bg-white border border-navy/10 p-5 sm:p-6" data-testid="mes-licencies">
            <div className="flex flex-wrap items-center gap-3">
              <p className="font-heading uppercase tracking-[0.25em] text-rouge text-xs">Mes licenciés</p>
              {data.licencies.map((l) => {
                const current = (selectedMember || data.member_id_courant) === l.member_id;
                return (
                  <button
                    key={l.member_id}
                    type="button"
                    onClick={() => { setError(""); setSelectedMember(l.member_id); }}
                    data-testid={`licencie-chip-${l.member_id.slice(0, 8)}`}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 text-xs font-heading uppercase tracking-wider border transition-[border-color,background-color,color] duration-300 ${
                      current ? "bg-navy text-white border-navy" : "bg-white text-navy/70 border-navy/20 hover:border-rouge hover:text-rouge"
                    }`}
                  >
                    {l.prenom} {l.nom}
                    {l.dossier && (
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${STATUT_STYLES[l.dossier.statut] || "bg-steel/15 text-steel"}`}>
                        {l.dossier.statut === "CATÉGORIE À VALIDER" ? "À valider" : l.dossier.statut === "INSCRIPTION VALIDÉE" ? "Validée" : l.dossier.statut}
                      </span>
                    )}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => setShowAdd(true)}
                data-testid="ajouter-licencie-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-xs font-heading uppercase tracking-wider border border-dashed border-rouge/50 text-rouge hover:bg-rouge hover:text-white transition-[background-color,color,border-color] duration-300"
              >
                + Ajouter un licencié
              </button>
            </div>
            <p className="mt-3 text-xs text-steel">Un même compte peut gérer plusieurs licenciés (conjoint, enfants…) — chaque dossier, document et paiement reste indépendant.</p>
          </div>
        )}
        {data?.saison_2027_statut === "PAIEMENT REÇU – EN ATTENTE DE VALIDATION" && (
          <div className="mb-8 bg-amber-50 border border-amber-300 text-amber-800 text-sm px-5 py-4" data-testid="adhesion-pending-banner">
            <p className="font-semibold">Votre paiement 2027 a bien été reçu.</p>
            <p>Votre inscription est actuellement en cours de vérification par le Club Cycliste Le Boulou.</p>
          </div>
        )}
        {data?.saison_2027_statut === "INSCRIPTION VALIDÉE" && (
          <div className="mb-8 bg-green-50 border border-green-300 text-green-800 text-sm px-5 py-4" data-testid="adhesion-validated-banner">
            <p className="font-semibold">Inscription 2027 : validée.</p>
          </div>
        )}
        {data?.dossier_2027?.statut === "CATÉGORIE À VALIDER" && (
          <div className="mb-8 bg-amber-50 border border-amber-300 text-amber-800 text-sm px-5 py-4" data-testid="categorie-attente-banner">
            <p className="font-semibold">Votre dossier 2027 est en cours de vérification par le Club Cycliste Le Boulou.</p>
            <p>Vous pourrez poursuivre votre inscription dès que votre catégorie aura été validée — vous recevrez un email.</p>
          </div>
        )}
        {data?.dossier_2027 && ["ecole_de_velo", "club"].includes(data.dossier_2027.categorie_2027) && !["paid"].includes(data.dossier_2027.payment_status) && (
          <div className="mb-8 bg-navy text-white p-6 sm:p-8 flex flex-wrap items-center justify-between gap-5" data-testid="continuer-inscription-banner">
            <div>
              <p className="font-heading uppercase font-semibold tracking-tight text-2xl">
                Catégorie validée : {data.dossier_2027.categorie_2027 === "ecole_de_velo" ? "École de vélo" : "Licencié club"}
              </p>
              <p className="mt-1 text-sm text-white/70">Vous pouvez maintenant finaliser votre inscription 2027 : licence, équipements, documents et paiement.</p>
            </div>
            <Link
              to={data.member_id_courant ? `/rejoindre?licencie=${data.member_id_courant}` : "/rejoindre"}
              data-testid="continuer-inscription-btn"
              className="inline-flex items-center gap-3 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-8 py-4 hover:bg-white hover:text-navy transition-[background-color,color] duration-300"
            >
              Continuer mon inscription 2027
              <ArrowRight size={16} />
            </Link>
          </div>
        )}
        {data?.peut_reinscription_2027 && !data?.dossier_2027 && (
          <div className="mb-8 bg-navy text-white p-6 sm:p-8 flex flex-wrap items-center justify-between gap-5" data-testid="reinscription-banner">
            <div>
              <p className="font-heading uppercase font-semibold tracking-tight text-2xl">Saison 2027</p>
              <p className="mt-1 text-sm text-white/70">
                Votre dossier 2026 est connu du club : vos informations seront pré-remplies, il ne vous restera qu'à choisir votre licence.
              </p>
            </div>
            <Link
              to={data.member_id_courant ? `/rejoindre?licencie=${data.member_id_courant}` : "/rejoindre"}
              data-testid="reinscription-2027-btn"
              className="inline-flex items-center gap-3 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-8 py-4 hover:bg-white hover:text-navy transition-[background-color,color] duration-300"
            >
              Se réinscrire pour 2027
              <ArrowRight size={16} />
            </Link>
          </div>
        )}
        {!data && !error ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-rouge" size={28} /></div>
        ) : data ? (
          <div className="grid lg:grid-cols-2 gap-6">
            <Section icon={User} title="Mes informations" testId="section-profil">
              <Row label="Nom" value={data.profil.nom} />
              <Row label="Prénom" value={data.profil.prenom} />
              <Row label="Adresse" value={`${data.profil.adresse}, ${data.profil.code_postal} ${data.profil.ville}`} />
              <Row label="Téléphone" value={data.profil.telephone} />
              <Row label="Email" value={data.profil.email} />
              <Row label="Date de naissance" value={data.profil.date_naissance} />
              <Row label="Numéro de licence" value={data.profil.numero_licence} />
              <Row label="NIP" value={data.profil.nip} />
              <Row label="UCI ID" value={data.profil.uci_id} />
              <Row label="Catégorie 2026" value={data.profil.categorie} />
            </Section>

            <Section icon={CreditCard} title="Mes saisons" testId="section-adhesion">
              {(data.saisons || []).map((s) => (
                <div key={s.saison} className="py-3 border-b border-navy/10 last:border-0" data-testid={`saison-${s.saison}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-heading uppercase font-semibold text-navy text-lg">Saison {s.saison}</p>
                    <span className={`text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 ${STATUT_STYLES[s.statut] || "bg-steel/15 text-steel"}`} data-testid={`saison-${s.saison}-statut`}>
                      {s.statut || "—"}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-steel">
                    {s.paiement?.total ? `Montant : ${s.paiement.total}` : ""}
                    {s.date_adhesion ? `${s.paiement?.total ? " · " : ""}Inscrit le ${fmtDate(s.date_adhesion)}` : ""}
                  </p>
                  {s.progression?.en_ligne && (
                    <div className="mt-2 space-y-1" data-testid={`saison-${s.saison}-progression`}>
                      <p className="text-xs flex items-center gap-2 text-navy/80">
                        <span className={s.progression.documents_ok ? "text-green-700 font-bold" : "text-steel"}>{s.progression.documents_ok ? "✓" : "○"}</span>
                        Documents validés
                      </p>
                      <p className="text-xs flex items-center gap-2 text-navy/80">
                        <span className={s.progression.paiement_ok ? "text-green-700 font-bold" : "text-steel"}>{s.progression.paiement_ok ? "✓" : "○"}</span>
                        Paiement reçu
                      </p>
                      <p className="text-xs flex items-center gap-2 text-navy/80">
                        <span className={s.progression.validee ? "text-green-700 font-bold" : "text-amber-600 font-bold"}>{s.progression.validee ? "✓" : "⏳"}</span>
                        {s.progression.validee ? "Inscription validée par le club" : "En attente de validation du club"}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </Section>

            <Section icon={Award} title="Mes licences" testId="section-licences">
              {!(data.saisons || []).some((s) => s.licences?.length > 0) ? (
                <p className="text-steel text-sm" data-testid="licences-empty">Aucune licence fédérale associée à votre inscription.</p>
              ) : (
                (data.saisons || []).map((s) =>
                  (s.licences || []).map((l) => (
                    <div key={`${s.saison}-${l.federation}`} className="py-3 border-b border-navy/10 last:border-0" data-testid={`licence-${s.saison}-${l.federation.toLowerCase()}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="font-heading uppercase font-semibold text-navy">
                          Licence {l.federation} {s.saison}
                          <span className="text-steel font-normal normal-case"> — {l.label}</span>
                        </p>
                        <span className={`text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 ${STATUT_STYLES[s.statut] || "bg-steel/15 text-steel"}`}>{s.statut}</span>
                      </div>
                      {l.numero && <p className="mt-1.5 text-xs text-navy/70">N° {l.numero}{l.etat ? ` — ${l.etat}` : ""}</p>}
                      <p className="mt-1.5 text-xs text-steel">
                        {l.federation === "FFC"
                          ? "Licence fédérale à finaliser ou consultable sur la plateforme officielle FFC."
                          : "Licence fédérale à finaliser ou consultable sur la plateforme officielle FSGT."}
                      </p>
                    </div>
                  ))
                )
              )}
            </Section>

            <Section icon={Wallet} title="Mes paiements" testId="section-paiements">
              {!(data.saisons || []).some((s) => s.paiement?.total || s.paiement?.statut) ? (
                <p className="text-steel text-sm" data-testid="paiements-empty">Aucun paiement enregistré pour le moment.</p>
              ) : (
                (data.saisons || []).filter((s) => s.paiement?.total || s.paiement?.statut).map((s, i) => (
                  <div key={s.saison} className="py-3 border-b border-navy/10 last:border-0" data-testid={`paiement-${i}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-navy font-medium text-sm">Inscription {s.saison}</p>
                      <p className="font-heading font-semibold text-navy">{s.paiement.total}</p>
                    </div>
                    <p className="mt-1 text-xs text-steel flex flex-wrap items-center gap-2">
                      {s.paiement.date ? fmtDate(s.paiement.date) : ""}
                      <span className={`text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 ${STATUT_STYLES[s.statut] || "bg-steel/15 text-steel"}`} data-testid={`paiement-${i}-statut`}>{s.statut}</span>
                    </p>
                  </div>
                ))
              )}
            </Section>

            <Section icon={Shirt} title="Mes équipements" testId="section-equipements">
              {!(data.saisons || []).some((s) => s.equipements?.length > 0) ? (
                <p className="text-steel text-sm" data-testid="equipements-empty">Aucun équipement commandé.</p>
              ) : (
                (data.saisons || []).filter((s) => s.equipements?.length > 0).map((s) =>
                  s.equipements.map((eq, i) => (
                    <div key={`${s.saison}-${i}`} className="flex flex-wrap items-center justify-between gap-2 py-3 border-b border-navy/10 last:border-0" data-testid={`equipement-${s.saison}-${i}`}>
                      <p className="text-navy font-medium text-sm">{eq.label} — taille {eq.taille} × {eq.quantite} <span className="text-steel text-xs">(saison {s.saison})</span></p>
                      <p className="font-heading font-semibold text-navy text-sm">{eq.total}</p>
                    </div>
                  ))
                )
              )}
            </Section>

            <Section icon={CalendarCheck} title="Mes inscriptions aux courses" testId="section-participations">
              {participations.length === 0 ? (
                <p className="text-steel text-sm" data-testid="participations-empty">Aucune demande de participation pour le moment. Depuis le calendrier, utilisez « M'inscrire à cette course ».</p>
              ) : (
                participations.map((p) => (
                  <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-3 border-b border-navy/10 last:border-0" data-testid={`participation-${p.id.slice(0, 8)}`}>
                    <div className="min-w-0">
                      <p className="text-navy font-medium text-sm">{p.event?.name || "Course"}</p>
                      <p className="mt-0.5 text-xs text-steel">{p.event?.date ? fmtDate(p.event.date) : ""}{p.event?.location ? ` · ${p.event.location}` : ""}</p>
                    </div>
                    <span className={`text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 ${p.statut === "CONFIRMÉE" ? "bg-green-100 text-green-700" : p.statut === "VALIDÉE" ? "bg-amber-100 text-amber-700" : "bg-steel/15 text-steel"}`}>{p.statut}</span>
                  </div>
                ))
              )}
            </Section>

            <Section icon={FileText} title="Mes documents" testId="section-documents">
              <DocGroup title="Documents du club" docs={docs.club} empty="Les documents du club seront disponibles ici prochainement." onView={viewDoc} onDownload={downloadDoc} groupTestId="documents-club" />
              <div className="mt-6">
                <DocGroup title="Mes documents personnels" docs={docs.personnel} empty="Aucun document personnel pour le moment." onView={viewDoc} onDownload={downloadDoc} groupTestId="documents-personnels" />
              </div>
            </Section>

            <div className="lg:col-span-2">
              <Section icon={Megaphone} title="Actualités licenciés" testId="section-posts">
                {data.posts.length === 0 ? (
                  <p className="text-steel text-sm" data-testid="posts-empty">Les informations réservées aux membres seront publiées ici par le club.</p>
                ) : (
                  data.posts.map((p) => (
                    <article key={p.id} className="py-4 border-b border-navy/10 last:border-0" data-testid={`post-${p.id}`}>
                      <p className="font-heading uppercase tracking-wider text-[11px] text-rouge mb-1">{fmtDate(p.date)}</p>
                      <h3 className="font-heading uppercase font-semibold text-navy text-lg">{p.title}</h3>
                      <p className="mt-2 text-sm text-navy/80 leading-relaxed whitespace-pre-line">{p.content}</p>
                    </article>
                  ))
                )}
              </Section>
            </div>
          </div>
        ) : null}
      </main>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-navy/70 flex items-center justify-center p-5" data-testid="ajouter-licencie-modal">
          <form onSubmit={submitAddLicencie} className="bg-white w-full max-w-lg p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
            <h2 className="font-heading uppercase font-semibold tracking-tight text-navy text-2xl mb-1">Ajouter un licencié</h2>
            <p className="text-sm text-steel mb-6">
              La personne sera rattachée à votre compte (ex. votre enfant). Son dossier partira en « CATÉGORIE À VALIDER » —
              vous finaliserez son inscription depuis son espace dédié.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="nl-nom" className="block text-[11px] font-heading uppercase tracking-wider text-steel mb-1.5">Nom *</label>
                <input id="nl-nom" data-testid="nl-nom" required value={addForm.nom} onChange={(e) => setAddForm({ ...addForm, nom: e.target.value })} className="w-full border border-navy/20 px-4 py-3 text-sm outline-none focus:border-rouge" />
              </div>
              <div>
                <label htmlFor="nl-prenom" className="block text-[11px] font-heading uppercase tracking-wider text-steel mb-1.5">Prénom *</label>
                <input id="nl-prenom" data-testid="nl-prenom" required value={addForm.prenom} onChange={(e) => setAddForm({ ...addForm, prenom: e.target.value })} className="w-full border border-navy/20 px-4 py-3 text-sm outline-none focus:border-rouge" />
              </div>
              <div>
                <label htmlFor="nl-naissance" className="block text-[11px] font-heading uppercase tracking-wider text-steel mb-1.5">Date de naissance *</label>
                <input id="nl-naissance" data-testid="nl-naissance" required type="date" value={addForm.date_naissance} onChange={(e) => setAddForm({ ...addForm, date_naissance: e.target.value })} className="w-full border border-navy/20 px-4 py-3 text-sm outline-none focus:border-rouge" />
              </div>
              <div>
                <label htmlFor="nl-tel" className="block text-[11px] font-heading uppercase tracking-wider text-steel mb-1.5">Téléphone</label>
                <input id="nl-tel" data-testid="nl-tel" value={addForm.telephone} onChange={(e) => setAddForm({ ...addForm, telephone: e.target.value })} className="w-full border border-navy/20 px-4 py-3 text-sm outline-none focus:border-rouge" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="nl-adresse" className="block text-[11px] font-heading uppercase tracking-wider text-steel mb-1.5">Adresse</label>
                <input id="nl-adresse" data-testid="nl-adresse" value={addForm.adresse} onChange={(e) => setAddForm({ ...addForm, adresse: e.target.value })} className="w-full border border-navy/20 px-4 py-3 text-sm outline-none focus:border-rouge" />
              </div>
              <div>
                <label htmlFor="nl-cp" className="block text-[11px] font-heading uppercase tracking-wider text-steel mb-1.5">Code postal</label>
                <input id="nl-cp" data-testid="nl-cp" value={addForm.code_postal} onChange={(e) => setAddForm({ ...addForm, code_postal: e.target.value })} className="w-full border border-navy/20 px-4 py-3 text-sm outline-none focus:border-rouge" />
              </div>
              <div>
                <label htmlFor="nl-ville" className="block text-[11px] font-heading uppercase tracking-wider text-steel mb-1.5">Ville</label>
                <input id="nl-ville" data-testid="nl-ville" value={addForm.ville} onChange={(e) => setAddForm({ ...addForm, ville: e.target.value })} className="w-full border border-navy/20 px-4 py-3 text-sm outline-none focus:border-rouge" />
              </div>
            </div>
            <label className="mt-5 flex items-start gap-3 text-xs text-navy/80 cursor-pointer" data-testid="nl-exactitude">
              <input type="checkbox" required checked={addForm.exactitude} onChange={(e) => setAddForm({ ...addForm, exactitude: e.target.checked })} className="mt-0.5 h-4 w-4 accent-[#D90429]" />
              Je certifie l'exactitude des informations de ce licencié.
            </label>
            <label className="mt-3 flex items-start gap-3 text-xs text-navy/80 cursor-pointer" data-testid="nl-conditions">
              <input type="checkbox" required checked={addForm.conditions} onChange={(e) => setAddForm({ ...addForm, conditions: e.target.checked })} className="mt-0.5 h-4 w-4 accent-[#D90429]" />
              J'accepte les conditions d'adhésion pour ce licencié.
            </label>
            <span className="mt-3 block bg-mist border border-navy/10 p-3" data-testid="nl-reglement">
              <label className="flex items-start gap-3 text-xs text-navy/80 cursor-pointer">
                <input type="checkbox" required checked={addForm.reglement} onChange={(e) => setAddForm({ ...addForm, reglement: e.target.checked })} data-testid="nl-reglement-check" className="mt-0.5 h-4 w-4 accent-[#D90429]" />
                <span>J'ai lu le règlement intérieur du club et je l'accepte pour ce licencié. *</span>
              </label>
              {reglementDoc && (
                <button
                  type="button"
                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/documents/inscription/${reglementDoc.id}/lire`, "_blank", "noopener")}
                  data-testid="nl-lire-reglement"
                  className="mt-2 ml-7 inline-flex items-center gap-2 text-[10px] font-heading uppercase tracking-wider text-rouge border border-rouge/40 px-3 py-1.5 hover:bg-rouge hover:text-white transition-colors duration-300"
                >
                  Lire le règlement
                </button>
              )}
            </span>
            <div className="mt-7 flex flex-wrap gap-3 justify-end">
              <button type="button" onClick={() => setShowAdd(false)} data-testid="nl-cancel" className="px-6 py-3 text-xs font-heading uppercase tracking-wider border border-navy/25 text-navy/70 hover:border-rouge hover:text-rouge transition-colors duration-300">
                Annuler
              </button>
              <button type="submit" disabled={addBusy} data-testid="nl-submit" className="inline-flex items-center gap-2 bg-rouge text-white px-6 py-3 text-xs font-heading uppercase tracking-wider hover:bg-navy disabled:opacity-60 transition-colors duration-300">
                {addBusy && <Loader2 size={14} className="animate-spin" />}
                Ajouter ce licencié
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
